import { useMemo } from 'react';
import { useGetEntitlementsListQuery } from 'src/store/services';
import { useGetUser, useSubscriptionPlans } from 'src/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { PlanVariant } from 'src/types/wallet/models/PlanVariant';
import { CanonicalInterval, UserTier } from 'src/types';
import type { SnEntitlementDetailLimits } from 'src/types/models/SnEntitlementDetailLimits';

type EntitlementsProps = {
  isBusiness?: boolean;
  interval: CanonicalInterval;
};

const availableIndividualTiers = [
  UserTier.SUPERNINJA_STANDARD,
  UserTier.SUPERNINJA_PRO,
  UserTier.SUPERNINJA_ULTRA,
  UserTier.SUPERNINJA_MAX,
];
const availableBusinessTiers = [
  UserTier.BUSINESS_SUPERNINJA_PRO,
  UserTier.BUSINESS_SUPERNINJA_ULTRA,
  UserTier.BUSINESS_SUPERNINJA_MAX,
];

export const useEntitlements = ({
  isBusiness,
  interval,
}: EntitlementsProps) => {
  const { user_id } = useGetUser();
  const { plans, isPlansLoading } = useSubscriptionPlans({
    group: isBusiness ? 'teams' : 'individual',
  });

  const planVariant = plans ? (plans[0].variant as string) : undefined;

  const canLoadData =
    !isPlansLoading && plans && plans?.length > 0 && !!planVariant;

  const dataArg = canLoadData
    ? {
        plan_variant: planVariant as PlanVariant,
        user_id: user_id,
      }
    : skipToken;

  const { data, isLoading, isFetching } = useGetEntitlementsListQuery(dataArg);

  const sumLimits = (limits: SnEntitlementDetailLimits): number => {
    const values = Object.entries(limits).map(([, v]) => v?.value ?? 0);

    if (values.some((v) => v === -1)) return -1;

    return values.reduce((acc, v) => acc + v, 0);
  };

  const entitlements = (data?.entitlements ?? [])
    .filter((item) =>
      (isBusiness ? availableBusinessTiers : availableIndividualTiers).includes(
        item.tier,
      ),
    )
    .filter(
      (item) => (item.interval as unknown as CanonicalInterval) === interval,
    )
    .map((item) => {
      const total = sumLimits(item.limits);
      return {
        ...item,
        limits: { ...item.limits, total: { value: total } },
      };
    });

  return useMemo(
    () => ({
      entitlements,
      isLoading: isLoading || isFetching,
    }),
    [entitlements, isLoading, isFetching],
  );
};
